<template>
  <v-navigation-drawer
    v-model="abierto"
    right
    fixed
    temporary
    hide-overlay
    :width="$vuetify.breakpoint.smAndDown ? '100%' : 440"
    class="asistente"
  >
    <!-- Cabecera -->
    <div class="asistente-cabecera">
      <v-icon color="#BA6B30" class="mr-2">mdi-creation</v-icon>
      <span class="asistente-titulo">Asistente</span>
      <v-spacer></v-spacer>
      <template v-if="terminosAceptados">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon small v-on="on" @click="alternar('capacidades')" :color="vista === 'capacidades' ? 'primary' : ''">
              <v-icon small>mdi-help-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>Qué puede hacer</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon small v-on="on" @click="abrirSugerencia" :color="vista === 'sugerir' ? 'primary' : ''">
              <v-icon small>mdi-lightbulb-on-outline</v-icon>
            </v-btn>
          </template>
          <span>Pedir algo que todavía no hace</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon small v-on="on" @click="alternar('historial')" :color="vista === 'historial' ? 'primary' : ''">
              <v-icon small>mdi-history</v-icon>
            </v-btn>
          </template>
          <span>Conversaciones anteriores</span>
        </v-tooltip>
      </template>
      <v-tooltip bottom v-if="terminosAceptados">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="nuevaConversacion" :disabled="!mensajes.length">
            <v-icon small>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Nueva conversación</span>
      </v-tooltip>
      <v-btn icon small @click="abierto = false"><v-icon small>mdi-close</v-icon></v-btn>
    </div>

    <!-- Términos: primera vez -->
    <div v-if="vista === 'terminos'" class="asistente-terminos">
      <template v-if="terminos.puntos">
        <div class="terminos-hero">
          <div class="terminos-hero-icono"><v-icon color="#BA6B30">mdi-creation</v-icon></div>
          <h3>Tu asistente para consultar el negocio</h3>
          <p>{{ terminos.intro }}</p>
        </div>
        <div v-for="p in terminos.puntos" :key="p.titulo" class="terminos-punto">
          <v-icon class="terminos-punto-icono" size="20">{{ p.icono }}</v-icon>
          <div>
            <div class="terminos-punto-titulo">{{ p.titulo }}</div>
            <div class="terminos-punto-texto">{{ p.texto }}</div>
          </div>
        </div>
      </template>
      <div v-else class="markdown" v-html="render(terminos.texto || '')"></div>
    </div>
    <div v-if="vista === 'terminos'" class="terminos-pie">
      <p v-if="terminos.cierre">{{ terminos.cierre }}</p>
      <v-btn block depressed large color="#BA6B30" dark class="text-none" :loading="aceptando" @click="aceptarTerminos">
        Entendido, empezar
      </v-btn>
      <div v-if="error" class="asistente-error">{{ error }}</div>
    </div>

    <!-- Capacidades -->
    <div v-else-if="vista === 'capacidades'" class="asistente-historial">
      <p class="asistente-bienvenida">Esto es lo que puedo consultar hoy. Tocá un ejemplo para probarlo.</p>
      <div v-for="c in capacidades" :key="c.nombre" class="capacidad" @click="enviar(c.ejemplo)">
        <span class="capacidad-titulo">{{ c.titulo }}</span>
        <span class="capacidad-ejemplo">{{ c.ejemplo }}</span>
      </div>
      <p class="asistente-nota">
        ¿Te falta algo? <a @click.prevent="abrirSugerencia">Pedilo acá</a> y lo evaluamos para una próxima versión.
      </p>
    </div>

    <!-- Pedir algo -->
    <div v-else-if="vista === 'sugerir'" class="asistente-historial">
      <p class="asistente-bienvenida">¿Qué te gustaría que el asistente pudiera hacer o responder?</p>
      <v-textarea v-model="sugerencia" outlined auto-grow rows="4" hide-details placeholder="Por ejemplo: que me diga cuánto le debo a cada proveedor"></v-textarea>
      <div class="d-flex mt-3">
        <v-btn text small @click="alternar('chat')">Cancelar</v-btn>
        <v-spacer></v-spacer>
        <v-btn depressed small color="#BA6B30" dark :disabled="!sugerencia.trim()" :loading="enviandoSugerencia" @click="enviarSugerencia">Enviar pedido</v-btn>
      </div>
      <p v-if="sugerenciaEnviada" class="asistente-nota">Gracias, quedó registrado. Los pedidos se revisan para decidir las próximas funciones.</p>
    </div>

    <!-- Historial -->
    <div v-else-if="vista === 'historial'" class="asistente-historial">
      <div v-if="!conversaciones.length" class="asistente-vacio">Todavía no hay conversaciones.</div>
      <div
        v-for="c in conversaciones"
        :key="c.id"
        class="historial-item"
        :class="{ activo: c.id === conversacionId }"
        @click="abrirConversacion(c.id)"
      >
        <span class="historial-titulo">{{ c.titulo || "(sin título)" }}</span>
        <span class="historial-fecha">{{ fechaCorta(c.actualizada) }}</span>
        <v-btn icon x-small @click.stop="borrar(c.id)"><v-icon x-small>mdi-delete-outline</v-icon></v-btn>
      </div>
    </div>

    <!-- Mensajes -->
    <div v-else ref="lista" class="asistente-mensajes">
      <div v-if="avisoCuota" class="asistente-cuota" :class="{ agotada: avisoCuota.agotada }">
        {{ avisoCuota.texto }}
      </div>
      <div v-if="!mensajes.length && !enviando" class="asistente-bienvenida">
        <p>Preguntá sobre los números de <b>{{ nombreEmpresa }}</b>. Consulto los reportes del sistema y te respondo con la cifra.</p>
        <div class="sugerencias">
          <button v-for="c in capacidades" :key="c.nombre" type="button" class="sugerencia" @click="enviar(c.ejemplo)">{{ c.ejemplo }}</button>
        </div>
        <p class="asistente-nota" v-if="estado && estado.consumo_mes">
          {{ estado.consumo_mes.mensajes }} de {{ estado.consumo_mes.cuota }} consultas usadas este mes.
        </p>
      </div>

      <div v-for="m in mensajes" :key="m.id || m.tmp" class="msg" :class="m.rol">
        <div v-if="m.rol === 'user'" class="burbuja">{{ m.contenido }}</div>
        <div v-else class="burbuja">
          <div class="markdown" v-html="render(m.contenido)"></div>
          <div v-if="m.tools_usadas && m.tools_usadas.length" class="tools">
            <v-chip v-for="(t, i) in m.tools_usadas" :key="i" x-small label outlined :color="t.ok ? 'grey' : 'red'" class="mr-1 mb-1">
              <v-icon x-small left>{{ t.ok ? "mdi-database-search-outline" : "mdi-alert-circle-outline" }}</v-icon>
              {{ describirTool(t) }}
            </v-chip>
          </div>
          <div v-if="m.id" class="acciones">
            <v-btn icon x-small :color="m.feedback === 'util' ? 'green' : ''" @click="feedback(m, 'util')"><v-icon x-small>mdi-thumb-up-outline</v-icon></v-btn>
            <v-btn icon x-small :color="m.feedback === 'no_util' ? 'red' : ''" @click="feedback(m, 'no_util')"><v-icon x-small>mdi-thumb-down-outline</v-icon></v-btn>
            <span v-if="m.incidencia" class="incidencia">{{ m.incidencia }}</span>
          </div>
          <div v-if="m.pidiendoComentario" class="comentario">
            <v-text-field
              v-model="m.comentario"
              dense outlined hide-details autofocus
              placeholder="¿Qué estuvo mal? (opcional)"
              @keydown.enter="confirmarFeedback(m)"
            >
              <template v-slot:append>
                <v-btn icon x-small @click="confirmarFeedback(m)"><v-icon small>mdi-send</v-icon></v-btn>
              </template>
            </v-text-field>
          </div>
        </div>
      </div>

      <div v-if="enviando" class="msg assistant">
        <div class="burbuja pensando">
          <v-progress-circular indeterminate size="14" width="2" color="#BA6B30" class="mr-2"></v-progress-circular>
          Consultando los reportes…
        </div>
      </div>
      <div v-if="error" class="asistente-error">{{ error }}</div>
    </div>

    <!-- Entrada -->
    <div class="asistente-entrada" v-if="vista === 'chat'">
      <v-textarea
        ref="input"
        v-model="texto"
        auto-grow rows="1" row-height="20" dense outlined hide-details
        placeholder="Preguntá algo… (Enter para enviar)"
        :disabled="enviando"
        @keydown.enter.exact.prevent="enviar()"
      ></v-textarea>
      <v-btn icon color="#BA6B30" :disabled="!texto.trim() || enviando" @click="enviar()">
        <v-icon>mdi-send</v-icon>
      </v-btn>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from "vuex";
import api from "@/api/asistente";
import { renderMarkdown } from "@/util/markdownSimple";

export default {
  name: "AsistenteDrawer",
  data() {
    return {
      abierto: false,
      vista: "chat", // chat | terminos | capacidades | sugerir | historial
      aceptando: false,
      sugerencia: "",
      enviandoSugerencia: false,
      sugerenciaEnviada: false,
      preguntaPendiente: "",
      conversaciones: [],
      conversacionId: null,
      mensajes: [],
      texto: "",
      enviando: false,
      error: "",
    };
  },
  computed: {
    ...mapState("auth", ["empresa"]),
    ...mapState("asistente", ["estado"]),
    terminos() {
      return (this.estado && this.estado.terminos) || {};
    },
    terminosAceptados() {
      return !!(this.estado && this.estado.terminos && this.estado.terminos.aceptados);
    },
    capacidades() {
      return (this.estado && this.estado.capacidades) || [];
    },
    // La cuota es un fusible, no un producto: se avisa al 80% y al 100% se pide ampliarla.
    avisoCuota() {
      const c = this.estado && this.estado.consumo_mes;
      if (!c || !c.cuota) return null;
      const restantes = c.cuota - c.mensajes;
      if (restantes <= 0) {
        return { agotada: true, texto: "Alcanzaste el límite de consultas de este mes. Escribinos si necesitás ampliarlo." };
      }
      if (c.mensajes / c.cuota >= 0.8) {
        return { agotada: false, texto: `Te quedan ${restantes} consultas este mes.` };
      }
      return null;
    },
    nombreEmpresa() {
      return (this.empresa && (this.empresa.nombreFantasia || this.empresa.name)) || "tu empresa";
    },
  },
  created() {
    // Cualquier pantalla puede abrirlo: this.$updateBus.$emit("asistente:abrir", { pregunta })
    this.$updateBus.$on("asistente:abrir", this.abrir);
  },
  beforeDestroy() {
    this.$updateBus.$off("asistente:abrir", this.abrir);
  },
  methods: {
    render: renderMarkdown,
    async abrir(opciones = {}) {
      this.abierto = true;
      if (!this.estado) await this.$store.dispatch("asistente/cargarEstado");
      if (!this.terminosAceptados) {
        this.vista = "terminos";
        this.preguntaPendiente = opciones.pregunta || "";
        return;
      }
      this.vista = "chat";
      if (opciones.pregunta) {
        this.enviar(opciones.pregunta);
      } else {
        this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
      }
    },
    async enviar(pregunta) {
      const texto = (pregunta || this.texto).trim();
      if (!texto || this.enviando) return;
      this.vista = "chat";
      this.texto = "";
      this.error = "";
      this.mensajes.push({ tmp: Date.now(), rol: "user", contenido: texto });
      this.enviando = true;
      this.scrollAbajo();
      try {
        const { data } = await api.chat(texto, this.conversacionId);
        this.conversacionId = data.conversacion_id;
        this.mensajes.push({ ...data.mensaje, comentario: "", pidiendoComentario: false });
        if (this.estado && this.estado.consumo_mes) this.estado.consumo_mes.mensajes += 1;
      } catch (e) {
        const detalle = e.response && e.response.data && e.response.data.error;
        this.error = detalle || "No pude responder. Probá de nuevo en un momento.";
        if (e.response && e.response.status === 409) this.conversacionId = null;
        if (e.response && e.response.data && e.response.data.codigo === "terminos") {
          await this.$store.dispatch("asistente/cargarEstado");
          this.vista = "terminos";
        }
      } finally {
        this.enviando = false;
        this.scrollAbajo();
        this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
      }
    },
    async aceptarTerminos() {
      this.aceptando = true;
      try {
        await api.aceptarTerminos();
        await this.$store.dispatch("asistente/cargarEstado");
        this.vista = "chat";
        if (this.preguntaPendiente) {
          const pregunta = this.preguntaPendiente;
          this.preguntaPendiente = "";
          this.enviar(pregunta);
        }
      } catch (e) {
        this.error = "No pude registrar la aceptación. Probá de nuevo.";
      } finally {
        this.aceptando = false;
      }
    },
    alternar(vista) {
      this.vista = this.vista === vista ? "chat" : vista;
      if (this.vista === "chat") this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
    abrirSugerencia() {
      // Se prellena con la última pregunta: casi siempre el pedido nace de un "no puedo"
      const ultima = [...this.mensajes].reverse().find((m) => m.rol === "user");
      if (!this.sugerencia && ultima) this.sugerencia = `Que pueda responder: "${ultima.contenido}"`;
      this.sugerenciaEnviada = false;
      this.vista = "sugerir";
    },
    async enviarSugerencia() {
      this.enviandoSugerencia = true;
      try {
        await api.sugerir(this.sugerencia.trim(), this.conversacionId);
        this.sugerencia = "";
        this.sugerenciaEnviada = true;
      } catch (e) {
        this.error = "No pude enviar el pedido.";
      } finally {
        this.enviandoSugerencia = false;
      }
    },
    nuevaConversacion() {
      this.conversacionId = null;
      this.mensajes = [];
      this.error = "";
      this.vista = "chat";
    },
    async cargarConversaciones() {
      try {
        const { data } = await api.conversaciones();
        this.conversaciones = data;
      } catch (e) {
        this.conversaciones = [];
      }
    },
    async abrirConversacion(id) {
      try {
        const { data } = await api.conversacion(id);
        this.conversacionId = data.id;
        this.mensajes = data.mensajes.map((m) => ({ ...m, comentario: "", pidiendoComentario: false }));
        this.vista = "chat";
        this.scrollAbajo();
      } catch (e) {
        this.error = "No pude abrir esa conversación.";
      }
    },
    async borrar(id) {
      await api.borrarConversacion(id).catch(() => {});
      this.conversaciones = this.conversaciones.filter((c) => c.id !== id);
      if (this.conversacionId === id) this.nuevaConversacion();
    },
    feedback(m, valor) {
      if (valor === "no_util" && m.feedback !== "no_util") {
        // El 👎 pide un comentario corto: es lo que alimenta la eval
        this.$set(m, "pidiendoComentario", true);
        this.$set(m, "feedback", valor);
        return;
      }
      this.$set(m, "feedback", valor);
      this.$set(m, "pidiendoComentario", false);
      api.feedback(m.id, valor).catch(() => {});
    },
    confirmarFeedback(m) {
      this.$set(m, "pidiendoComentario", false);
      api.feedback(m.id, "no_util", m.comentario || "").catch(() => {});
    },
    describirTool(t) {
      const a = t.argumentos || {};
      const partes = [];
      if (a.desde && a.hasta) partes.push(a.desde === a.hasta ? a.desde : `${a.desde} → ${a.hasta}`);
      if (a.anio1) partes.push(`${a.anio1}${a.anio2 ? " vs " + a.anio2 : ""}`);
      if (a.sucursal) partes.push(a.sucursal);
      if (a.busqueda) partes.push(`"${a.busqueda}"`);
      return `${t.nombre.replace(/_/g, " ")}${partes.length ? " · " + partes.join(" · ") : ""}`;
    },
    fechaCorta(iso) {
      const d = new Date(iso);
      return isNaN(d) ? "" : d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" });
    },
    scrollAbajo() {
      this.$nextTick(() => {
        const el = this.$refs.lista;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
  watch: {
    vista(val) {
      if (val === "historial") this.cargarConversaciones();
    },
  },
};
</script>

<style scoped>
.asistente {
  display: flex;
  flex-direction: column;
  z-index: 150;
  /* Colores relativos al tema: con grises fijos el texto desaparece en modo oscuro */
  --asis-texto: rgba(0, 0, 0, 0.87);
  --asis-secundario: rgba(0, 0, 0, 0.6);
  --asis-terciario: rgba(0, 0, 0, 0.45);
  --asis-borde: rgba(0, 0, 0, 0.08);
  --asis-fondo: rgba(0, 0, 0, 0.045);
  --asis-acento: #8a4d1f;
  --asis-error: #b00020;
}
.asistente.theme--dark {
  --asis-texto: rgba(255, 255, 255, 0.92);
  --asis-secundario: rgba(255, 255, 255, 0.7);
  --asis-terciario: rgba(255, 255, 255, 0.5);
  --asis-borde: rgba(255, 255, 255, 0.12);
  --asis-fondo: rgba(255, 255, 255, 0.07);
  --asis-acento: #e0a070;
  --asis-error: #ff8a80;
}
.asistente ::v-deep .v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
}
.asistente-cabecera {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid var(--asis-borde);
}
.asistente-titulo {
  font-weight: 600;
  letter-spacing: 0.3px;
}
.asistente-mensajes,
.asistente-historial {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}
.asistente-bienvenida {
  color: var(--asis-secundario);
  font-size: 14px;
  padding: 8px 4px;
}
.sugerencias {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 10px 0;
}
.sugerencia {
  border: 1px solid rgba(186, 107, 48, 0.4);
  color: var(--asis-acento);
  background: rgba(186, 107, 48, 0.06);
  border-radius: 14px;
  padding: 4px 10px;
  font-size: 12.5px;
  cursor: pointer;
  text-align: left;
}
.sugerencia:hover {
  background: rgba(186, 107, 48, 0.14);
}
.asistente-nota {
  font-size: 12px;
  color: var(--asis-terciario);
  margin-top: 8px;
}
.msg {
  display: flex;
  margin-bottom: 10px;
}
.msg.user {
  justify-content: flex-end;
}
.burbuja {
  max-width: 92%;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.45;
  word-break: break-word;
}
.msg.user .burbuja {
  background: #0f0f0f;
  color: #fff;
  border-bottom-right-radius: 4px;
  white-space: pre-wrap;
}
.msg.assistant .burbuja {
  background: var(--asis-fondo);
  border-bottom-left-radius: 4px;
  width: 92%;
}
.burbuja.pensando {
  display: flex;
  align-items: center;
  color: var(--asis-terciario);
  font-style: italic;
}
.markdown ::v-deep p { margin: 0 0 6px; }
.markdown ::v-deep p:last-child { margin-bottom: 0; }
.markdown ::v-deep ul, .markdown ::v-deep ol { margin: 4px 0 6px; padding-left: 20px; }
.markdown ::v-deep h4, .markdown ::v-deep h5, .markdown ::v-deep h6 { margin: 6px 0 4px; font-size: 14px; }
.markdown ::v-deep code { background: var(--asis-fondo); padding: 0 4px; border-radius: 3px; font-size: 13px; }
.markdown ::v-deep table { border-collapse: collapse; margin: 6px 0; font-size: 13px; width: 100%; }
.markdown ::v-deep th, .markdown ::v-deep td { border: 1px solid var(--asis-borde); padding: 3px 6px; text-align: left; }
.markdown ::v-deep th { background: var(--asis-fondo); }
.tools {
  margin-top: 6px;
}
.acciones {
  display: flex;
  align-items: center;
  margin-top: 4px;
  opacity: 0.55;
}
.acciones:hover {
  opacity: 1;
}
.incidencia {
  font-size: 11px;
  color: var(--asis-error);
  margin-left: 6px;
}
.comentario {
  margin-top: 6px;
}
.asistente-cuota {
  font-size: 12.5px;
  color: var(--asis-acento);
  background: rgba(186, 107, 48, 0.08);
  border-radius: 8px;
  padding: 6px 10px;
  margin-bottom: 10px;
}
.asistente-cuota.agotada {
  color: var(--asis-error);
  background: rgba(176, 0, 32, 0.06);
}
.asistente-error {
  color: var(--asis-error);
  font-size: 13px;
  padding: 6px 4px;
}
.asistente-entrada {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid var(--asis-borde);
}
.historial-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 6px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13.5px;
}
.historial-item:hover, .historial-item.activo {
  background: var(--asis-fondo);
}
.historial-titulo {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.historial-fecha {
  color: var(--asis-terciario);
  font-size: 12px;
}
.asistente-terminos {
  flex: 1;
  overflow-y: auto;
  padding: 20px 20px 8px;
  font-size: 13.5px;
  color: var(--asis-texto);
}
.terminos-hero {
  text-align: center;
  margin-bottom: 20px;
}
.terminos-hero-icono {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(186, 107, 48, 0.14);
  margin-bottom: 10px;
}
.terminos-hero h3 {
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 6px;
}
.terminos-hero p {
  color: var(--asis-secundario);
  font-size: 13.5px;
  line-height: 1.5;
  margin: 0;
}
.terminos-punto {
  display: flex;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid var(--asis-borde);
}
.terminos-punto-icono {
  color: #BA6B30 !important;
  margin-top: 1px;
}
.terminos-punto-titulo {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 2px;
}
.terminos-punto-texto {
  color: var(--asis-secundario);
  line-height: 1.45;
}
.terminos-pie {
  padding: 12px 20px 16px;
  border-top: 1px solid var(--asis-borde);
}
.terminos-pie p {
  font-size: 12.5px;
  color: var(--asis-terciario);
  text-align: center;
  margin-bottom: 10px;
}
.capacidad {
  display: flex;
  flex-direction: column;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 4px;
}
.capacidad:hover {
  background: rgba(186, 107, 48, 0.08);
}
.capacidad-titulo {
  font-weight: 600;
  font-size: 13.5px;
}
.capacidad-ejemplo {
  font-size: 12.5px;
  color: var(--asis-acento);
}
.asistente-vacio {
  color: var(--asis-terciario);
  font-size: 13px;
  padding: 8px;
}
</style>
